import { createLog } from '@iv/shared/src/log';
import { parse } from '@iv/shared/src/querystring';
import axios from 'axios';

const log = createLog('DataProcessor');

class DataProcessor {
  private config: ICfg;
  /**
   * 外部配置的获取互动配置的方法，约定如下：
   * curl: http 请求器
   * param: 首次请求 param 为空，该方法需要处理 param 为空的逻辑，这里通常指的是起播的时候
   *        后续的请求，互动引擎会带上 metaId 和 nodeId
   * resolve: 返回符合互动数据标准的数据
   * config: function(curl, param?: {metaId: string, nodeId: string}) {
   *  return new Promise(function(resolve, reject) {
   *    resolve(cfg);
   *  }
   * }
   */
  private getRemoteConfig: Function;

  private globalInteractIds: Set<string> = new Set();

  /**
   * 获取配置信息
   * 1. 如果外部传入了配置就使用外部传入的
   * 2. 否则，从地址上获取远程JSON配置的地址并且拉取配置信息
   * @param cfg
   */
  public async getConfig(cfg?: ICfg | Function) {
    if (cfg) {
      if (typeof cfg === 'function') {
        this.getRemoteConfig = cfg;
        const res = await this.getRemoteConfig(axios);
        return this.setConfig(res);
      }
      return this.setConfig(cfg);
    }
    const jsonUrl = this.getJSONUrl();
    log('remote json url:', jsonUrl);
    if (jsonUrl) {
      const res = await axios.get(jsonUrl);
      return this.setConfig(res?.data ? res.data : {});
    }
    throw new Error('jsonUrl is empty!');
  }

  /**
   *
   * 获取剧id
   * @memberof DataProcessor
   */
  public getMetaId() {
    return this.config.meta.id;
  }

  /**
   * 根据 nodeId 获取和组装 nodeInfo
   * 不传 nodeId 默认取第一个 node 节点
   * @param nodeId
   */
  public async getNodeInfo(nodeId?: string) {
    const cfg = this.config;
    let currentNode: ICfgNode;
    if (nodeId) {
      const metaId = cfg.meta.id || '';
      // 无 nodeId 有对应节点数据则拉取
      if (!cfg.nodeListMap[nodeId]) {
        const res = await this.getRemoteConfig(axios, {
          nodeId,
          metaId,
        });
        this.setConfig(res);
      }
      currentNode = { ...cfg.nodeListMap[nodeId] };
    } else {
      currentNode = { ...cfg.nodeList[0] };
    }
    currentNode.components = [];
    currentNode.interacts = Array.from(this.globalInteractIds)
      .concat(currentNode.interactIds || [])
      .map((interactId) => {
        const interact = cfg.interactListMap[interactId];
        currentNode.components.push(cfg.componentListMap[interact.componentId]);
        return interact;
      });
    return currentNode;
  }

  /**
   * 获取后续节点的播放 uri
   * @param nodeId
   */
  public async getNextNodeListUri(nodeId: string) {
    const cfg = this.config;
    const metaId = cfg.meta.id || '';
    if (!cfg.nodeListMap[nodeId]) {
      return [];
    }
    const currentNode = cfg.nodeListMap[nodeId];
    // 判断下一个节点是否有数据，没有则做异步补拉一次数据
    const extendConfig = currentNode.extendConfig || {};
    const isNeedGetConfig = extendConfig.nextNodeList?.some((nextNodeId: string) => !cfg.nodeListMap[nextNodeId]);
    if (isNeedGetConfig) {
      const res = await this.getRemoteConfig(axios, {
        nodeId,
        metaId,
      });
      this.setConfig(res);
    }

    // 取出后续播放节点的 uri
    const ids: string[] = [];
    const nextNodeList = extendConfig.nextNodeList || [];
    nextNodeList.forEach((nextNodeId: string) => {
      const nextNodeInfo = cfg.nodeListMap[nextNodeId];
      if (nextNodeInfo) {
        ids.push(nextNodeInfo.uri);
      }
    });
    return ids;
  }

  /**
   * 查找 nodeInfo
   * @param nodeInfo
   */
  public findNodeInfo(nodeInfo: IVideoInfo) {
    const targetNode = this.config.nodeList.find((node) => {
      if (nodeInfo.startTime > 0) {
        return node.uri === nodeInfo.uri && node.startTime === nodeInfo.startTime;
      }
      return node.uri === nodeInfo.uri;
    });
    return targetNode && this.getNodeInfo(targetNode.id);
  }

  public getVariableList() {
    return this.config.variableList || [];
  }

  /**
   * 检查并设置 config
   * @param config
   */
  private setConfig(config: ICfg) {
    if (this.checkAndMapConfig(config)) {
      // 设置全局组件
      const { interactList } = this.config;
      interactList.forEach((interact) => {
        // type为2表示全局组件，不为2默认都是视频节点内互动组件，不做特殊处理
        if (interact.type === 2) {
          this.globalInteractIds.add(interact.id);
        }
      });
      return this.config;
    }
    throw new Error('config is incomplete!');
  }

  /**
   * 获取引用 JSSDK 地址传递进来的 JSON 地址参数
   */
  private getJSONUrl() {
    const scripts = Array.from(document.getElementsByTagName('script'));
    let src = '';
    const len = scripts.length;
    for (let i = 0; i < len; i += 1) {
      const script = scripts[i];
      if (/\/iv\.global(\.prod)?\.js/.test(script.src)) {
        ({ src } = script);
        break;
      }
    }
    const query = parse(src);
    return query.json || '';
  }

  /**
   * 数组结构数据转Map
   * @param param
   */
  private toMap(param: { config: ICfg; field: string; isAdd: boolean; cb?: Function }) {
    this.config[`${param.field}Map`] = this.config[`${param.field}Map`] || {};
    param.config[param.field].forEach((f: any) => {
      if (!this.config[`${param.field}Map`][f.id]) {
        this.config[`${param.field}Map`][f.id] = f;
        param.isAdd && this.config[param.field].push(f);
      }
      if (param.cb) {
        param.cb(f);
      }
    });
  }

  /**
   * 把一些通用字段信息，塞进 extendConfig 中提供到组件使用
   * @param cfg
   */
  private setCommonField(cfg: any) {
    const config = cfg;
    const interactConfig = config.interactConfig || {};
    ['startTime', 'endTime', 'duration'].forEach((ecf) => {
      if (typeof interactConfig[ecf] === 'undefined') {
        interactConfig[ecf] = config[ecf];
      }
    });
    config.interactConfig = interactConfig;
  };

  /**
   * 检查配置信息是否完整
   * 并且把互动核心数据从 array 转为 map 形式，方便后面的逻辑处理
   * @param cfg
   */
  private checkAndMapConfig(cfg: ICfg) {
    if (!(cfg?.meta && cfg.nodeList && Array.isArray(cfg.nodeList) && cfg.nodeList.length > 0)) {
      return false;
    }

    if (!this.config) { // 如果是首次设置config，先做一次初始化
      this.config = cfg;
      this.toMap({ config: cfg, field: 'nodeList', isAdd: false });
      this.toMap({
        config: cfg,
        field: 'interactList',
        isAdd: false,
        cb: (f: any) => {
          this.setCommonField(f);
        },
      });
      this.toMap({ config: cfg, field: 'componentList', isAdd: false });
    }

    const interactIds = new Set();
    const componentIds = new Set();
    // 节点List转Map
    this.toMap({
      config: cfg,
      field: 'nodeList',
      isAdd: true,
      cb: (f: any) => {
        (f.interactIds || []).forEach(interactIds.add, interactIds);
      },
    });
    // 互动List转Map
    this.toMap({
      config: cfg,
      field: 'interactList',
      isAdd: true,
      cb: (f: any) => {
        componentIds.add(f.componentId);
        this.setCommonField(f);
      },
    });

    // 节点使用的互动ID如果不存在，则认为数据不完整
    const checkInteract = Array.from(interactIds).every((id: string) => this.config.interactListMap[id]);
    if (!checkInteract) {
      return false;
    }

    this.toMap({
      config: cfg,
      field: 'componentList',
      isAdd: true,
    });
    // 互动使用的组件ID如果不存在，则认为数据不完整
    const checkComponent = Array.from(componentIds).every((id: string) => this.config.componentListMap[id]);
    if (!checkComponent) {
      return false;
    }

    return true;
  }
}

export default DataProcessor;
